import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const fmtQty  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PRIMARY = '#4a148c';
const PRIMARY_MID = '#8e24aa';
const PRIMARY_LT = '#f3e5f5';
const th  = { padding: '8px 10px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td  = { padding: '7px 10px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '7px 10px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };
const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const StockMovementReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [search, setSearch]     = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'StockReportApi',
        `/StockMovementItemDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch stock movement report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.Name    || '').toLowerCase().includes(q) ||
      (r.BatchNumber || '').toLowerCase().includes(q) ||
      (r.TransType   || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // Compute running balance per item (grouped by Name + BatchNumber)
  const filteredWithBalance = useMemo(() => {
    const balanceMap = {};
    return filtered.map(r => {
      const key = `${r.Name || ''}||${r.BatchNumber || ''}`;
      const inQty  = parseFloat(r.InQty)  || 0;
      const outQty = parseFloat(r.OutQty) || 0;
      if (!(key in balanceMap)) {
        balanceMap[key] = parseFloat(r.OpeningBalance) || 0;
      }
      balanceMap[key] = balanceMap[key] + inQty - outQty;
      return { ...r, ComputedBalance: balanceMap[key] };
    });
  }, [filtered]);

  // Totals
  const totalIn  = filteredWithBalance.reduce((s, r) => s + (parseFloat(r.InQty)  || 0), 0);
  const totalOut = filteredWithBalance.reduce((s, r) => s + (parseFloat(r.OutQty) || 0), 0);
  // Sum of final balance per unique item/batch
  const totalBal = useMemo(() => {
    const lastBalMap = {};
    filteredWithBalance.forEach(r => {
      const key = `${r.Name || ''}||${r.BatchNumber || ''}`;
      lastBalMap[key] = r.ComputedBalance;
    });
    return Object.values(lastBalMap).reduce((s, v) => s + v, 0);
  }, [filteredWithBalance]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'StockMovement', 0);
    if (!filteredWithBalance.length) return;
    const cols = ['#', 'Item Name', 'Batch', 'Date', 'Trans Type', 'In Qty', 'Out Qty', 'Balance'];
    const rows = filteredWithBalance.map((r, i) => [
      i + 1, r.Name, r.BatchNumber || '-', fmtDate(r.TransactionDate),
      r.TransType || '-',
      parseFloat(r.InQty) || 0,
      parseFloat(r.OutQty) || 0,
      r.ComputedBalance,
    ]);

      await exportStyledExcel({
          reportTitle: 'STOCK MOVEMENT REPORT',
          sheetName: 'Stock Movement',
          fileName: `StockMovementReport_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: cols,
          rows,
          totalsRow: ['', '', '', '', 'Totals', totalIn, totalOut, totalBal],
          columnWidths: [4,28,14,12,16,10,10,12],
      });
  };

  const transTypeColor = (t) => {
    const v = (t || '').toLowerCase();
    if (v.includes('sale') || v.includes('out'))     return { bg: '#fce7f3', color: '#be185d' };
    if (v.includes('purchase') || v.includes('in'))  return { bg: '#d1fae5', color: '#059669' };
    if (v.includes('transfer'))                       return { bg: '#dbeafe', color: '#1d4ed8' };
    if (v.includes('adjust'))                         return { bg: '#fef3c7', color: '#d97706' };
    return { bg: '#f3f4f6', color: '#6b7280' };
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading stock movement report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #6a1b9a 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Stock Movement Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search item, batch, type..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filteredWithBalance.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 750 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Item Name</th>
                <th style={th}>Batch</th>
                <th style={th}>Date</th>
                <th style={{ ...th, textAlign: 'center' }}>Trans Type</th>
                <th style={{ ...th, textAlign: 'right' }}>In Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Out Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {filteredWithBalance.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (
                filteredWithBalance.map((r, i) => {
                  const tc = transTypeColor(r.TransType);
                  const inQty  = parseFloat(r.InQty)  || 0;
                  const outQty = parseFloat(r.OutQty) || 0;
                  const bal    = r.ComputedBalance;
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 600, color: '#1e293b' }}>{r.Name}</td>
                      <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{r.BatchNumber || '-'}</td>
                      <td style={td}>{fmtDate(r.TransactionDate)}</td>
                      <td style={tdC}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: tc.bg, color: tc.color }}>
                          {r.TransType || '-'}
                        </span>
                      </td>
                      <td style={{ ...tdR, color: inQty > 0 ? '#059669' : '#94a3b8', fontWeight: inQty > 0 ? 600 : 400 }}>
                        {inQty > 0 ? `+${fmtQty(inQty)}` : '-'}
                      </td>
                      <td style={{ ...tdR, color: outQty > 0 ? '#dc2626' : '#94a3b8', fontWeight: outQty > 0 ? 600 : 400 }}>
                        {outQty > 0 ? `-${fmtQty(outQty)}` : '-'}
                      </td>
                      <td style={{ ...tdR, fontWeight: 700, color: bal >= 0 ? '#1c2266' : '#dc2626' }}>
                        {fmtQty(bal)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filteredWithBalance.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={{ ...tdR, color: '#059669' }}>+{fmtQty(totalIn)}</td>
                  <td style={{ ...tdR, color: '#dc2626' }}>-{fmtQty(totalOut)}</td>
                  <td style={{ ...tdR, color: PRIMARY }}>{fmtQty(totalBal)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockMovementReport;




